import React, { useState } from 'react';
import Select from 'react-select';
import LoaderSpinner from '../loaderspinner';

export interface SearchableSelectOption {
  value: string;
  label: string;
  id?: string | number;
}

export interface SearchableFloatingSelectProps {
  label: string;
  name: string;
  value: SearchableSelectOption | null;
  options: SearchableSelectOption[];
  onChange: (option: SearchableSelectOption | null) => void;
  className?: string;
  error?: string;
  disabled?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  placeholder?: string;
}

const SearchableFloatingSelect: React.FC<SearchableFloatingSelectProps> = ({
  label,
  name,
  value,
  options,
  onChange,
  className = '',
  error,
  disabled,
  isDisabled,
  isLoading = false,
  placeholder = '',
}) => {
  const hasContent = Boolean(value);
  const [isFocused, setIsFocused] = useState(false);
  
  const isSelectDisabled = disabled || isDisabled || isLoading;
  const shouldFloat = hasContent || isFocused || Boolean(error);

  const customStyles = {
    control: (provided: any, state: any) => {
      const baseStyles = {
        ...provided,
        borderRadius: '50px',
        minHeight: '48px',
        backgroundColor: state.isDisabled ? '#f3f4f6' : '#fff',
        paddingLeft: '20px',
        paddingRight: '20px',
      };

      // Error state overrides everything
      if (error) {
        return {
          ...baseStyles,
          borderColor: '#ef4444 !important', // Force red border
          boxShadow: state.isFocused ? '0 0 0 2px #fecaca' : 'none',
          '&:hover': {
            borderColor: '#ef4444 !important', // Keep red on hover
          }
        };
      }

      // Normal states when no error
      return {
        ...baseStyles,
        borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
        boxShadow: state.isFocused ? '0 0 0 2px #bfdbfe' : 'none',
      };
    },
    menu: (provided: any) => ({
      ...provided,
      borderRadius: '20px',
      maxHeight: 300,
      zIndex: 22,
      overflow: 'hidden',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      color: state.isSelected ? '#fff' : '#111827',
      backgroundColor: state.isSelected
        ? '#3b82f6'
        : state.isFocused
        ? '#f3f4f6'
        : '#fff',
      cursor: 'pointer',
    }),
    loadingIndicator: () => ({
      display: 'none',
    }),
  };

  return (
    <div className={`relative mb-6 rounded-[50px] ${className}`}>
      <Select
        inputId={name}
        name={name}
        value={value}
        options={options}
        onChange={onChange}
        isDisabled={isSelectDisabled}
        isLoading={false}
        placeholder={placeholder || ''}
        styles={customStyles}
        classNamePrefix="react-select"
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        isSearchable
        isClearable
        menuPlacement='auto'
        components={{
          IndicatorSeparator: () => null,
          LoadingIndicator: () => null,
        }}
      />
      
      {/* Custom Loading Spinner */}
      {isLoading && (
        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center justify-center" style={{ zIndex: 25 }}>
          <LoaderSpinner size={20} />
        </div>
      )}
      
      <label
        htmlFor={name}
        className={`absolute left-5 transition-all duration-200 pointer-events-none
          ${shouldFloat ? 'top-0 -translate-y-1/2 text-xs font-medium bg-white px-2 scale-85' : 'top-1/2 -translate-y-1/2'}
          ${error ? 'text-red-500' : isFocused ? 'text-blue-600' : isLoading ? 'text-gray-400' : 'text-gray-500'}
        `}
        style={{ zIndex: 21 }}
      >
        {label}
      </label>
      
      {error && <div className="mt-1 text-red-500 text-sm">{error}</div>}
    </div>
  );
};

export default SearchableFloatingSelect;
